/**
 * Shopify API Integration for Lens Configurator
 * Sistema completo con access token para modificar precios reales
 *
 * ⚠️ SEGURIDAD: Este archivo contiene lógica sensible
 * - Access token debe configurarse de forma segura (no hardcodeado)
 * - Llamadas API deshabilitadas por defecto por seguridad
 * - Usar solo en entornos de producción con configuración apropiada
 */

// @ts-nocheck

if (typeof ShopifyLensAPI === 'undefined') {
class ShopifyLensAPI {
  constructor() {
    // ⚠️ SEGURIDAD: Access token debe venir de variable de entorno o configuración segura
    // NUNCA hardcodear tokens en código fuente
    this.accessToken = window.Shopify?.accessToken || null; // Debe venir de configuración segura
    this.enableRealAPICalls = false; // 🔥 DESHABILITADO por seguridad - solo visual updates
    
    // Detectar el dominio de la tienda Shopify correctamente
    this.shopDomain = this.getShopifyDomain();
    this.apiVersion = '2024-04';
    
    // Llamada directa a la API (sin proxy, funciona en entorno Shopify)
    this.baseUrl = `https://${this.shopDomain}/admin/api/${this.apiVersion}`;
    
    console.log('🚀 ShopifyLensAPI initialized with DIRECT API access:', {
      shopDomain: this.shopDomain,
      baseUrl: this.baseUrl,
      enableRealAPICalls: this.enableRealAPICalls,
      hasToken: !!this.accessToken
    });
  }

  getShopifyDomain() {
    // Si estamos en localhost o dev, usar el dominio desde Shopify
    if (window.location.hostname.includes('localhost') || 
        window.location.hostname.includes('127.0.0.1') ||
        window.location.hostname.includes('ngrok')) {
      
      // Buscar el dominio en elementos de Shopify
      const shopifyScripts = document.querySelectorAll('script[src*=".myshopify.com"]');
      for (let script of shopifyScripts) {
        const match = script.src.match(/\/\/([^.]+\.myshopify\.com)/);
        if (match) {
          return match[1];
        }
      }
      
      // Buscar en meta tags
      const shopifyMeta = document.querySelector('meta[name="shopify-digital-wallet"]');
      if (shopifyMeta && shopifyMeta.content) {
        const match = shopifyMeta.content.match(/([^.]+\.myshopify\.com)/);
        if (match) {
          return match[1];
        }
      }
      
      // Buscar en window.Shopify
      if (window.Shopify && window.Shopify.shop) {
        return window.Shopify.shop;
      }
      
      // Fallback: intentar obtener de la URL actual si tiene parámetros Shopify
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('shop')) {
        return urlParams.get('shop');
      }
      
            // Último fallback: usar tu dominio específico
      console.warn('No se pudo detectar el dominio de Shopify, usando dominio específico');
      return 'gf7q0f-ma.myshopify.com';  // Tu tienda específica
    }
    
    // Si estamos en producción, usar el dominio actual
    return window.location.hostname;
  }

  async updateVariantPrice(variantId, newPrice, lensConfig, forceReal = false) {
    try {
      console.log('Updating variant price:', { variantId, newPrice, forceReal });

      // SIEMPRE hacer llamada real en dominio de Shopify
      const isShopifyDomain = window.location.hostname.includes('myshopify.com') || 
                             window.location.hostname.includes('shopifypreview.com');
      const shouldMakeRealCall = forceReal || isShopifyDomain || 
                                (!window.location.hostname.includes('localhost') && 
                                 !window.location.hostname.includes('127.0.0.1'));

      console.log('🔍 Call decision:', {
        hostname: window.location.hostname,
        isShopifyDomain,
        forceReal,
        shouldMakeRealCall: this.enableRealAPICalls
      });

      if (this.enableRealAPICalls && this.accessToken) {
        console.log('🌐 Making REAL API call to Shopify Admin API...');
        
        try {
          // Preparar datos para la API
          const variantData = {
            variant: {
              id: variantId,
              price: newPrice.toFixed(2),
              metafields: this.createMetafields(lensConfig)
            }
          };

          console.log('🌐 API Request:', {
            url: `${this.baseUrl}/variants/${variantId}.json`,
            data: variantData,
            shop: this.shopDomain
          });

          // Hacer request a la API de Shopify
          const response = await fetch(`${this.baseUrl}/variants/${variantId}.json`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Access-Token': this.accessToken
            },
            body: JSON.stringify(variantData)
          });

          if (response.ok) {
            const result = await response.json();
            console.log('✅ Variant updated successfully via API:', result);
            
            // También actualizar visualmente
            this.updatePagePrices(newPrice);
            
            return true;
          } else {
            const error = await response.text();
            console.error('❌ API Error Response:', {
              status: response.status,
              statusText: response.statusText,
              error: error
            });
            
            // Fallback a actualización visual
            console.log('🔄 Falling back to visual update...');
            this.updatePagePrices(newPrice);
            
            return false;
          }
        } catch (apiError) {
          console.error('💥 API Call Failed:', apiError);
          
          // Fallback a actualización visual
          console.log('🔄 Falling back to visual update...');
          this.updatePagePrices(newPrice);
          
          return false;
        }
      } else {
        // Fallback: solo actualización visual
        // API calls disabled, updating visually only
        this.updatePagePrices(newPrice);
        
        // Disparar evento personalizado
        document.dispatchEvent(new CustomEvent('shopify:variant-price-change', {
          detail: {
            variantId,
            newPrice,
            lensConfig,
            timestamp: new Date().toISOString()
          }
        }));
        
        return true;
      }

    } catch (error) {
      console.error('💥 Error updating variant:', error);
      return false;
    }
  }

  createMetafields(lensConfig) {
    const metafields = [];

    // Metafield para configuración completa
    metafields.push({
      key: 'lens_configuration',
      value: JSON.stringify(lensConfig),
      type: 'json'
    });

    // Metafields individuales para facilitar filtros
    if (lensConfig.lensType) {
      metafields.push({
        key: 'lens_type',
        value: typeof lensConfig.lensType === 'string' ? lensConfig.lensType : lensConfig.lensType.name || 'Desconocido',
        type: 'single_line_text_field'
      });
    }

    if (lensConfig.material) {
      metafields.push({
        key: 'lens_material',
        value: typeof lensConfig.material === 'string' ? lensConfig.material : lensConfig.material.name || 'Desconocido',
        type: 'single_line_text_field'
      });
    }

    return metafields;
  }

  async createCustomVariant(productId, baseVariant, lensConfig, totalPrice) {
    try {
      const variantTitle = this.generateVariantTitle(lensConfig);
      
      const newVariant = {
        variant: {
          product_id: productId,
          title: variantTitle,
          price: totalPrice.toFixed(2),
          sku: `${baseVariant.sku}-LENS-${Date.now()}`,
          inventory_management: 'shopify',
          inventory_quantity: 10,
          metafields: this.createMetafields(lensConfig)
        }
      };

      const response = await fetch(`${this.baseUrl}/products/${productId}/variants.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Access-Token': this.accessToken
        },
        body: JSON.stringify(newVariant)
      });

      if (response.ok) {
        const result = await response.json();
        console.log('New variant created:', result);
        return result.variant;
      } else {
        const error = await response.text();
        console.error('Error creating variant:', error);
        return null;
      }

    } catch (error) {
      console.error('Error creating custom variant:', error);
      return null;
    }
  }

  generateVariantTitle(lensConfig) {
    let title = 'Cristales';

    if (lensConfig.lensType) {
      const lensTypeName = typeof lensConfig.lensType === 'string' ? lensConfig.lensType : lensConfig.lensType.name || 'Desconocido';
      title += ` ${lensTypeName}`;
    }

    if (lensConfig.material) {
      const materialName = typeof lensConfig.material === 'string' ? lensConfig.material : lensConfig.material.name || 'Desconocido';
      title += ` - ${materialName}`;
    }

    if (lensConfig.treatments && lensConfig.treatments.length > 0) {
      const treatments = lensConfig.treatments.slice(0, 2).map(t => typeof t === 'string' ? t : t.name || 'Desconocido').join(', ');
      title += ` + ${treatments}`;
      if (lensConfig.treatments.length > 2) {
        title += ` (+${lensConfig.treatments.length - 2} más)`;
      }
    }

    return title;
  }

  async updateProductPricing(productId, variantId, lensConfig, basePrice) {
    const lensPrice = this.calculateLensPrice(lensConfig);
    const totalPrice = (basePrice / 100) + lensPrice; // basePrice viene en centavos

    console.log('Price calculation:', {
      basePrice: basePrice / 100,
      lensPrice,
      totalPrice
    });

    // Opción 1: Actualizar variante existente
    const updated = await this.updateVariantPrice(variantId, totalPrice, lensConfig);
    
    if (updated) {
      // Actualizar precios en la página inmediatamente
      this.updatePagePrices(totalPrice);
      
      // Guardar configuración localmente
      this.saveLensConfiguration(productId, variantId, lensConfig, totalPrice);
      
      return { success: true, method: 'updated', price: totalPrice };
    }

    // Opción 2: Si no se puede actualizar, crear nueva variante (opcional)
    // const baseVariant = await this.getVariant(variantId);
    // if (baseVariant) {
    //   const newVariant = await this.createCustomVariant(productId, baseVariant, lensConfig, totalPrice);
    //   if (newVariant) {
    //     return { success: true, method: 'created', variant: newVariant, price: totalPrice };
    //   }
    // }

    return { success: false, error: 'No se pudo actualizar el precio' };
  }

  calculateLensPrice(lensConfig) {
    let total = 0;

    // Nota: Los precios ahora vienen de productos de Shopify dinámicamente
    // Este método necesita ser actualizado para calcular precios desde productos addon
    console.log('Calculating lens price with new structure:', lensConfig);

    // Por ahora, devolver 0 hasta que se implemente el cálculo dinámico
    return total;
  }

  updatePagePrices(newPrice) {
    const priceSelectors = [
      '.price',
      '.product-price', 
      '.money',
      '[data-price]',
      '.price-current',
      '.price__current',
      '.product-form__price'
    ];
    
    const formattedPrice = this.formatPrice(newPrice);
    
    priceSelectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(el => {
        // Actualizar data attributes
        if (el.dataset.price) {
          el.dataset.price = Math.round(newPrice * 100);
        }
        
        // Actualizar contenido de texto si parece ser un precio
        if (this.looksLikePrice(el.textContent)) {
          el.textContent = formattedPrice;
        }
      });
    });

    // Actualizar botones de comprar
    const buyButtons = document.querySelectorAll('button[type="submit"], .btn-product, [data-add-to-cart]');
    buyButtons.forEach(btn => {
      if (btn.textContent.includes('$') || btn.textContent.includes('€')) {
        btn.textContent = btn.textContent.replace(/[\d,.]+ *[$€£¥]/, formattedPrice);
      }
      btn.dataset.customPrice = newPrice;
    });

    // Evento personalizado para notificar cambio de precio
    window.dispatchEvent(new CustomEvent('lensConfigPriceUpdated', {
      detail: { newPrice, formattedPrice }
    }));
  }

  looksLikePrice(text) {
    return /[\d,.]+ *[$€£¥]/.test(text) || /\$[\d,.]+/.test(text);
  }

  formatPrice(price, currency = 'USD') {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(price);
  }

  saveLensConfiguration(productId, variantId, lensConfig, totalPrice) {
    const config = {
      productId,
      variantId,
      lensConfig,
      totalPrice,
      timestamp: Date.now()
    };
    
    localStorage.setItem('currentLensConfig', JSON.stringify(config));
    localStorage.setItem(`lensConfig_${productId}`, JSON.stringify(config));
    
    console.log('Lens configuration saved:', config);
  }

  async loadLensConfiguration(productId) {
    const saved = localStorage.getItem(`lensConfig_${productId}`);
    if (saved) {
      const config = JSON.parse(saved);
      // Verificar que no esté muy antigua (24 horas)
      if (Date.now() - config.timestamp < 24 * 60 * 60 * 1000) {
        return config;
      }
    }
    return null;
  }

  // Método para restaurar precio original
  async restoreOriginalPrice(productId, variantId) {
    try {
      // Buscar precio original en data attributes o API
      const originalPriceEl = document.querySelector('[data-original-price]');
      const originalPrice = originalPriceEl ? 
        parseFloat(originalPriceEl.dataset.originalPrice) : 
        await this.getOriginalPrice(variantId);

      if (originalPrice) {
        await this.updateVariantPrice(variantId, originalPrice, {});
        this.updatePagePrices(originalPrice);
        
        // Limpiar configuración guardada
        localStorage.removeItem('currentLensConfig');
        localStorage.removeItem(`lensConfig_${productId}`);
        
        return true;
      }
    } catch (error) {
      console.error('Error restoring original price:', error);
    }
    return false;
  }

  async getOriginalPrice(variantId) {
    try {
      const response = await fetch(`${this.baseUrl}/variants/${variantId}.json`, {
        headers: {
          'X-Shopify-Access-Token': this.accessToken
        }
      });

      if (response.ok) {
        const result = await response.json();
        return parseFloat(result.variant.price);
      }
    } catch (error) {
      console.error('Error getting original price:', error);
    }
    return null;
  }

  // Inicialización de eventos
  initShopifyIntegration() {
    // Guardar precio original al cargar
    const priceEl = document.querySelector('.price, .product-price');
    if (priceEl && !priceEl.dataset.originalPrice) {
      const priceText = priceEl.textContent.replace(/[^\d.,]/g, '');
      const price = parseFloat(priceText.replace(',', '.'));
      if (!isNaN(price)) {
        priceEl.dataset.originalPrice = price;
      }
    }

    // Interceptar formularios de producto
    document.addEventListener('submit', this.handleProductFormSubmit.bind(this));
    
    // Cargar configuración guardada si existe
    this.loadSavedConfiguration();
  }

  handleProductFormSubmit(e) {
    if (e.target.matches('[action*="/cart/add"]')) {
      const lensConfig = JSON.parse(localStorage.getItem('currentLensConfig') || '{}');
      if (lensConfig.lensConfig) {
        // Agregar propiedades de configuración al formulario
        const properties = this.createCartProperties(lensConfig.lensConfig, lensConfig.totalPrice);
        
        Object.entries(properties).forEach(([key, value]) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = `properties[${key}]`;
          input.value = value;
          e.target.appendChild(input);
        });
      }
    }
  }

  createCartProperties(lensConfig, totalPrice) {
    const properties = {};

    if (lensConfig.lensType) {
      properties['_tipo_de_cristal'] = typeof lensConfig.lensType === 'string' ? lensConfig.lensType : lensConfig.lensType.name || 'Desconocido';
      // Nota: Precio se calcula dinámicamente, no está disponible aquí
    }

    if (lensConfig.material) {
      properties['_material'] = typeof lensConfig.material === 'string' ? lensConfig.material : lensConfig.material.name || 'Desconocido';
    }

    if (lensConfig.treatments && lensConfig.treatments.length > 0) {
      properties['_tratamientos'] = lensConfig.treatments.map(t => typeof t === 'string' ? t : t.name || 'Desconocido').join(', ');
    }

    if (lensConfig.prescription) {
      // Guardar resumen o indicador interno sin exponer campos individuales
      properties['_prescripcion_resumen'] = JSON.stringify(Object.keys(lensConfig.prescription));
    }

    properties['_Configuración Aplicada'] = new Date().toLocaleString();
    properties['_Precio Total Configurado'] = this.formatPrice(totalPrice);

    return properties;
  }

  async loadSavedConfiguration() {
    const productId = this.getCurrentProductId();
    if (productId) {
      const savedConfig = await this.loadLensConfiguration(productId);
      if (savedConfig) {
        this.updatePagePrices(savedConfig.totalPrice);
        console.log('Loaded saved lens configuration:', savedConfig);
      }
    }
  }

  getCurrentProductId() {
    // Extraer product ID de la página
    const productMeta = document.querySelector('meta[property="product:id"]');
    if (productMeta) return productMeta.content;
    
    const productInput = document.querySelector('input[name="product-id"], [data-product-id]');
    if (productInput) return productInput.value || productInput.dataset.productId;
    
    // Fallback: extraer de URL
    const match = window.location.pathname.match(/\/products\/[\w-]+\?.*product_id=(\d+)/);
    return match ? match[1] : null;
  }
}

// Hacer disponible globalmente
window.ShopifyLensAPI = ShopifyLensAPI;

// Auto-inicializar
document.addEventListener('DOMContentLoaded', () => {
  if (!window.shopifyLensAPI) {
    window.shopifyLensAPI = new ShopifyLensAPI();
    window.shopifyLensAPI.initShopifyIntegration();
    console.log('Shopify Lens API initialized with access token');
  }
});

}